import React, { useState } from "react";
import { Button, FormGroup, Label, Input } from "reactstrap";
import PropTypes from "prop-types";

const FormField = ({ fieldData, value, onChange }) => {
  const name = fieldData.name;

  if (fieldData.type === "select") {
    return (
      <Input
        type="select"
        name={name}
        value={value}
        onChange={(e) => onChange(name, e.target.value)}
      >
        {fieldData.options &&
          fieldData.options.map((option, index) => (
            <option key={index} value={option.value} disabled={option.disabled}>
              {option.label}
            </option>
          ))}
      </Input>
    );
  }

  if (fieldData.type === "radios") {
    return (
      <>
        {fieldData.options.map((option, index) => (
          <FormGroup check key={index}>
            <Label check htmlFor={`${name}-${option.value}`}>
              <Input
                type="radio"
                name={name}
                id={`${name}-${option.value}`}
                value={option.value}
                checked={value === option.value}
                onChange={(e) => onChange(name, e.target.value)}
              />
              {option.label}
            </Label>
          </FormGroup>
        ))}
      </>
    );
  }

  if (fieldData.type === "checkbox") {
    return (
      <FormGroup check>
        <Label check htmlFor={name}>
          <Input
            type="checkbox"
            name={name}
            id={name}
            checked={!!value}
            onChange={(e) => onChange(name, e.target.checked)}
          />
          {fieldData.label}
        </Label>
      </FormGroup>
    );
  }

  return (
    <Input
      type={fieldData.type || "text"}
      name={name}
      value={value}
      onChange={(e) => onChange(name, e.target.value)}
    />
  );
};

const FormBlock = ({ fieldData, value, onChange }) => {
  if (fieldData.type === "checkbox") {
    return <FormField fieldData={fieldData} value={value} onChange={onChange} />;
  }

  return (
    <FormGroup>
      <Label htmlFor={fieldData.name}>
        {fieldData.label}
        {fieldData.required && <span className="required">*</span>}
      </Label>
      <FormField fieldData={fieldData} value={value} onChange={onChange} />
    </FormGroup>
  );
};

const SchemaForm = ({
  formFields = [],
  validate,
  submit,
  reset,
  submitButtonColor = "primary",
  submitButtonLabel = "Submit",
  resetButtonLabel = "Reset",
  renderButtons,
  ...rest
}) => {
  const buildInitialValues = () => {
    const values = {};
    if (Array.isArray(formFields)) {
      formFields.forEach((field) => {
        values[field.name] = field.value || "";
      });
    } else if (formFields && typeof formFields === "object") {
      Object.keys(formFields).forEach((i) => {
        Object.keys(formFields[i]).forEach((j) => {
          if (Array.isArray(formFields[i][j])) {
            formFields[i][j].forEach((field) => {
              values[field.name] = field.value || "";
            });
          }
        });
      });
    }
    return values;
  };

  const [values, setValues] = useState(buildInitialValues);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});

  const handleChange = (name, value) => {
    setValues((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (validate) {
      const validationErrors = validate(values);
      if (validationErrors && Object.keys(validationErrors).length > 0) {
        setErrors(validationErrors);
        return;
      }
    }

    setErrors({});
    setIsSubmitting(true);

    const actions = {
      setSubmitting: setIsSubmitting,
    };

    submit(values, actions);
  };

  const handleReset = () => {
    const initial = buildInitialValues();
    setValues(initial);
    setErrors({});
    if (reset) {
      reset(initial, { setSubmitting: setIsSubmitting });
    }
  };

  const renderFields = () => {
    if (Array.isArray(formFields)) {
      return formFields.map((fieldData, index) => (
        <FormBlock
          key={index}
          fieldData={fieldData}
          value={values[fieldData.name]}
          onChange={handleChange}
        />
      ));
    }

    return Object.keys(formFields).map((i) => (
      <div key={i}>
        <h3>{formFields[i].section_label}</h3>
        {Object.keys(formFields[i]).map((j) => {
          if (Array.isArray(formFields[i][j])) {
            return formFields[i][j].map((fieldData, index) => (
              <FormBlock
                key={index}
                fieldData={fieldData}
                value={values[fieldData.name]}
                onChange={handleChange}
              />
            ));
          }
          return null;
        })}
      </div>
    ));
  };

  return (
    <form onSubmit={handleSubmit} {...rest}>
      {renderFields()}
      {renderButtons ? (
        renderButtons({
          handleSubmit,
          handleReset,
          values,
          isSubmitting,
          errors,
          formFields,
        })
      ) : (
        <>
          <Button
            type="submit"
            disabled={isSubmitting}
            color={submitButtonColor}
          >
            {submitButtonLabel}
          </Button>
          {reset && <Button onClick={handleReset}>{resetButtonLabel}</Button>}
        </>
      )}
    </form>
  );
};

SchemaForm.propTypes = {
  formFields: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
  submit: PropTypes.func.isRequired,
  reset: PropTypes.func,
  validate: PropTypes.func,
  submitButtonColor: PropTypes.string,
  submitButtonLabel: PropTypes.string,
  resetButtonLabel: PropTypes.string,
  renderButtons: PropTypes.func,
};

export default SchemaForm;
